import React, { useState } from 'react';
import { times } from 'lodash';
import moment from 'moment/moment';
import { Box } from '../../../../../../../CommonComponents/OPL/Box/Box';
import { Text } from '../../../../../../../CommonComponents/OPL/Text/Text';
import { Icon } from '../../../../../../../CommonComponents/OPL/Icon/Icon';
import ErrorState from '../../../../../../components/ErrorState/ErrorState';
import { TableLoading } from '../TableLoading/TableLoading';
import { formatCurrency, formatNumber } from '../../../../../../../CommonUtils/formatNumber';
import { HtmlTable } from '../../../../../../../CommonComponents/OPL/HtmlTable/HtmlTable';
import { useTransactions } from '../../../../../../hooks/useTransactions';
import { TruncatedText } from '../../../../../../../CommonComponents/OPL/Text/TruncatedText';
import { Modal } from '../../../../../../../CommonComponents/OPL/Modal/Modal';
import { ModalHeader } from '../../../../../../../CommonComponents/OPL/Modal/ModalHeader';
import { ModalCloseButton } from '../../../../../../../CommonComponents/OPL/Modal/ModalCloseButton';
import { ModalBody } from '../../../../../../../CommonComponents/OPL/Modal/ModalBody';
import { ClickableTableRow } from './MobileTransactions.styles';
import { TransactionDTO } from '../../../../../../api/useTransactionsQuery';

export const MobileTransactionsTable = () => {
  const [selectedTransaction, setSelectedTransaction] = useState<TransactionDTO | null>(null);

  const {
    data: { ...transactionsData },
    isFetching,
    isError,
    refetch,
    isFetchingNextPage,
  } = useTransactions();

  return (
    <>
      <HtmlTable columnSpacing="small" variant="embedded">
        <table css="table-layout: fixed">
          <thead>
            <tr>
              <th>
                <Text variant="smallCaps" textAlign="left">
                  Name
                </Text>
              </th>
              <th>
                <Text variant="smallCaps" textAlign="right" pr="24">
                  Total
                </Text>
              </th>
            </tr>
          </thead>

          <tbody>
            {isError && (
              <tr>
                <td>
                  <ErrorState
                    onRetry={() => {
                      refetch();
                    }}
                  />
                </td>
              </tr>
            )}

            {!isFetching && transactionsData?.data && transactionsData.data[0].length === 0 && (
              <tr>
                <td colSpan={2}>
                  <Text>There are no transactions available.</Text>
                </td>
              </tr>
            )}

            {isFetching && !isFetchingNextPage
              ? times(5).map(key => <TableLoading key={key} />)
              : transactionsData?.data &&
                transactionsData.data.map(page =>
                  page.map((transaction, index) => (
                    <ClickableTableRow
                      // eslint-disable-next-line react/no-array-index-key
                      key={index}
                      onClick={() => {
                        setSelectedTransaction(transaction);
                      }}
                    >
                      <td>
                        <Box display="flex" flexDirection="column">
                          <TruncatedText fontWeight={500} color="neutral.700" width="100%">
                            {transaction.name}
                          </TruncatedText>

                          <TruncatedText
                            fontWeight={400}
                            fontSize="small"
                            color="neutral.500"
                            width="100%"
                          >
                            {transaction.description}
                          </TruncatedText>
                        </Box>
                      </td>

                      <td>
                        <Box display="flex" justifyContent="flex-end" alignItems="center">
                          <Box display="flex" flexDirection="column">
                            <Text fontWeight={500} variant="tabular" textAlign="right">
                              {transaction.total &&
                                formatCurrency(transaction.total, { decimalPlaces: 2 })}
                            </Text>

                            <Text fontWeight={400} fontSize="small" textAlign="right">
                              {moment(transaction.date).format('M/D/YYYY')}
                            </Text>
                          </Box>
                          <Icon className="fas fa-chevron-right" color="neutral.400" ml={12} />
                        </Box>
                      </td>
                    </ClickableTableRow>
                  )),
                )}
            {isFetchingNextPage && times(5).map(key => <TableLoading key={key} />)}
          </tbody>
        </table>
      </HtmlTable>

      <Modal
        isOpen={!!selectedTransaction}
        onRequestClose={() => {
          setSelectedTransaction(null);
        }}
        variant="bottom"
      >
        <ModalHeader>{selectedTransaction?.name}</ModalHeader>
        <ModalCloseButton
          onClick={() => {
            setSelectedTransaction(null);
          }}
        />

        <ModalBody css="padding:0">
          <HtmlTable variant="embedded">
            <table>
              <tbody>
                <tr>
                  <td>
                    <TruncatedText fontWeight={500} textAlign="left" width="100%">
                      Date
                    </TruncatedText>
                  </td>

                  <td>
                    <Text fontWeight={500} textAlign="right">
                      {moment(selectedTransaction?.date).format('M/D/YYYY')}
                    </Text>
                  </td>
                </tr>

                <tr>
                  <td>
                    <TruncatedText fontWeight={500} textAlign="left" width="100%">
                      Description
                    </TruncatedText>
                  </td>

                  <td>
                    <Text fontWeight={500} textAlign="right">
                      {selectedTransaction?.description}
                    </Text>
                  </td>
                </tr>

                <tr>
                  <td>
                    <TruncatedText fontWeight={500} textAlign="left" width="100%">
                      Units
                    </TruncatedText>
                  </td>

                  <td>
                    <Text fontWeight={500} variant="tabular" textAlign="right">
                      {selectedTransaction?.units &&
                        formatNumber(selectedTransaction.units, { decimalPlaces: 2 })}
                    </Text>
                  </td>
                </tr>

                <tr>
                  <td>
                    <TruncatedText fontWeight={500} textAlign="left" width="100%">
                      Price
                    </TruncatedText>
                  </td>

                  <td>
                    <Text fontWeight={500} variant="tabular" textAlign="right">
                      {selectedTransaction?.price &&
                        formatCurrency(selectedTransaction.price, { decimalPlaces: 2 })}
                    </Text>
                  </td>
                </tr>

                <tr>
                  <td>
                    <TruncatedText fontWeight={500} textAlign="left" width="100%">
                      Total
                    </TruncatedText>
                  </td>

                  <td>
                    <Text fontWeight={500} variant="tabular" textAlign="right">
                      {selectedTransaction?.total &&
                        formatCurrency(selectedTransaction.total, { decimalPlaces: 2 })}
                    </Text>
                  </td>
                </tr>
              </tbody>
            </table>
          </HtmlTable>
        </ModalBody>
      </Modal>
    </>
  );
};
